import { CounterInput } from "@/components/counter-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetSenderProfilePlans } from "@/hooks/use-subscription";
import { ISenderProfilePlan, PaymentInterval, SubscriptionStatus } from "@/types/subscription.types";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";

export default function SeedListCancelInitiateDialog() {
  const [showCancelModal, setShowCancelModal] = useState(false);
  return (
    <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
      <DialogTrigger asChild>
        <Button variant="link" className="text-blue-500 underline">
          Cancel Plan
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel Your Plan</DialogTitle>
          <DialogDescription>
            Select the seed list plan you want to cancel.
          </DialogDescription>
        </DialogHeader>
        <SeedListCancelPlanContent setShowCancelModal={setShowCancelModal} />
      </DialogContent>
    </Dialog>
  );
}

interface SenderProfilePlanDisplay {
  planId: string;
  subscriptionId: string;
  uniqueId: string;
  name: string;
  espTypes: string[];
  seedCountPerSender: number;
  quantity: number;
  interval: PaymentInterval;
  status: SubscriptionStatus;
  maxSenderProfilesMonthly: number;
  usedSenderProfilesMonthly: number;
}

const SeedListCancelPlanContent = ({
  setShowCancelModal,
}: {
  setShowCancelModal: (show: boolean) => void;
}) => {
  const [cancelCount, setCancelCount] = useState(1);
  const [selectedPlanToCancel, setSelectedPlanToCancel] =
    useState<SenderProfilePlanDisplay | null>(null);

  const { data: senderProfilePlansResponse } = useGetSenderProfilePlans();
  const navigate = useNavigate();

  // Format sender profile plans for display
  const seedListPlans = useMemo(() => {
    const plans: SenderProfilePlanDisplay[] = [];

    if (senderProfilePlansResponse?.plans && senderProfilePlansResponse.plans.length > 0) {
      senderProfilePlansResponse.plans.forEach((plan: ISenderProfilePlan) => {
        const uniqueId = `${plan.subscriptionId}_${plan.planDetails._id}_${plan.interval}`;
        plans.push({
          planId: plan.planDetails._id,
          subscriptionId: plan.subscriptionId,
          uniqueId,
          name: plan.planDetails.planName,
          espTypes: plan.espTypes,
          seedCountPerSender: plan.seedCountPerSender,
          quantity: plan.quantity,
          interval: plan.interval,
          status: plan.status,
          maxSenderProfilesMonthly: plan.maxSenderProfilesMonthly,
          usedSenderProfilesMonthly: plan.usedSenderProfilesMonthly,
        });
      });
    }

    return plans;
  }, [senderProfilePlansResponse]);

  // Handler for cancel plan submission
  const handleCancelPlan = () => {
    if (!selectedPlanToCancel) return;

    navigate({
      to: "/cancelSeedListPlan",
      search: {
        subscriptionId: selectedPlanToCancel.subscriptionId,
        planId: selectedPlanToCancel.planId,
        quantity: cancelCount,
      },
    });
  };

  const isSubmitDisabled =
    !selectedPlanToCancel || cancelCount > selectedPlanToCancel.quantity;

  return (
    <>
      <div className="py-4 space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Select Seed List Plan</label>
          <Select
            value={selectedPlanToCancel?.uniqueId}
            onValueChange={(value) => {
              const plan = seedListPlans.find((p) => p.uniqueId === value);

              if (plan) {
                setSelectedPlanToCancel(plan);
                // Reset cancel count when changing plans
                setCancelCount(plan.quantity);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a seed list plan to cancel">
                <span className="text-left">
                  {selectedPlanToCancel
                    ? `${selectedPlanToCancel.name} (${selectedPlanToCancel.interval})`
                    : "Select a seed list plan to cancel"}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {seedListPlans.length === 0 ? (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                  No plans found
                </div>
              ) : (
                seedListPlans.map((plan) => (
                  <SelectItem
                    key={plan.uniqueId}
                    value={plan.uniqueId}
                    disabled={plan.status === SubscriptionStatus.PENDING}
                    wrapInItemText={false}
                  >
                    <div className="w-full pr-8 flex items-center justify-between">
                      <div>
                        <div className="flex items-center justify-between w-full">
                          <span className="font-medium">{plan.name}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>{plan.interval}</span>
                          <span>•</span>
                          <span>{plan.espTypes.join(", ")}</span>
                          <span>•</span>
                          <span>{plan.seedCountPerSender} seeds/sender</span>
                          <span>•</span>
                          <span>Qty: {plan.quantity}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        {plan.status === SubscriptionStatus.PENDING && (
                          <Badge
                            variant="default"
                            className="text-xs font-normal ml-2 bg-red-100 text-red-500 hover:bg-red-100 hover:text-red-500 border-none shadow-none"
                          >
                            Unpaid
                          </Badge>
                        )}
                        {plan.status === SubscriptionStatus.ACTIVE && (
                          <Badge
                            variant="default"
                            className="text-xs font-normal ml-2 bg-green-100 text-green-500 hover:bg-green-100 hover:text-green-500 border-none shadow-none"
                          >
                            Active
                          </Badge>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {selectedPlanToCancel && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Seed acc to Cancel</label>
            <CounterInput
              title=""
              count={cancelCount}
              setCount={setCancelCount}
              min={1}
              max={selectedPlanToCancel.quantity}
            />
            <p className="text-xs text-muted-foreground mt-1">
              You currently have {selectedPlanToCancel.quantity} sender profile{selectedPlanToCancel.quantity > 1 ? "s" : ""}. You
              cannot cancel more than your current quantity.
            </p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => setShowCancelModal(false)}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleCancelPlan}
          disabled={isSubmitDisabled}
        >
          Continue to Cancellation
        </Button>
      </DialogFooter>
    </>
  );
};

